/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Link } from '@tanstack/react-router'
import { ArrowRight, Zap, Layers, Code2, Gauge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthModalStore } from '@/stores/auth-modal-store'
import { Button } from '@/components/ui/button'
import { AnimateInView } from '@/components/animate-in-view'
import { HeroTerminalDemo } from '../hero-terminal-demo'

interface HeroProps {
  className?: string
  isAuthenticated?: boolean
}

export function Hero(props: HeroProps) {
  const { t } = useTranslation()
  const { openSignUp, openSignIn } = useAuthModalStore()

  const features = [
    {
      icon: <Layers className='size-4 text-red-500' />,
      title: t('8+ Upstream Adapters'),
      desc: t('Supports DeepSeek, Zhiyuan, Tongyi, Wenxin and more'),
    },
    {
      icon: <Code2 className='size-4 text-blue-500' />,
      title: t('40+ Model Access'),
      desc: t('Seamless integration, one-line code switch'),
    },
    {
      icon: <Zap className='size-4 text-amber-500' />,
      title: t('Quick Integration'),
      desc: t('Compatible with OpenAI SDK standard protocol'),
    },
    {
      icon: <Gauge className='size-4 text-emerald-500' />,
      title: t('Smart Routing · 99.9%'),
      desc: t('Automatic failover, stable switching'),
    },
  ]

  return (
    <section className='relative z-10 overflow-hidden px-6 pt-28 pb-16 md:pt-32 md:pb-24'>
      {/* Radial gradient background */}
      <div
        aria-hidden
        className='pointer-events-none absolute inset-0 -z-10 opacity-25 dark:opacity-[0.12]'
        style={{
          background: [
            'radial-gradient(ellipse 60% 50% at 20% 20%, oklch(0.72 0.18 25 / 80%) 0%, transparent 70%)',
            'radial-gradient(ellipse 50% 40% at 80% 15%, oklch(0.65 0.15 15 / 60%) 0%, transparent 70%)',
            'radial-gradient(ellipse 40% 35% at 40% 80%, oklch(0.70 0.12 35 / 40%) 0%, transparent 70%)',
          ].join(', '),
        }}
      />
      {/* Grid pattern */}
      <div
        aria-hidden
        className='absolute inset-0 -z-10 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_30%,black_20%,transparent_100%)] bg-[size:4rem_4rem] opacity-[0.08]'
      />

      <div className='mx-auto max-w-6xl'>
        {/* Top section: left text + right terminal */}
        <div className='flex flex-col items-center gap-12 md:flex-row md:items-center md:gap-16'>
          {/* Left: title + buttons */}
          <div className='flex flex-1 flex-col items-center text-center md:items-start md:text-left'>
            <h1
              className='landing-animate-fade-up text-[clamp(2rem,5vw,3rem)] leading-[1.15] font-bold tracking-tight'
              style={{ animationDelay: '0ms' }}
            >
              {t('All-in-X Token')}{' '}
              <span className='text-red-600 dark:text-red-500'>
                {t('Hub')}
              </span>
            </h1>
            <p
              className='landing-animate-fade-up text-muted-foreground/80 mt-4 max-w-md text-base leading-relaxed opacity-0 md:text-lg'
              style={{ animationDelay: '80ms' }}
            >
              {t('Connect to global AI models')}
            </p>
            <div
              className='landing-animate-fade-up mt-8 flex items-center gap-4 opacity-0'
              style={{ animationDelay: '160ms' }}
            >
              {props.isAuthenticated ? (
                <Button
                  className='group rounded-xl bg-red-600 px-6 py-5 text-base font-semibold text-white shadow-lg shadow-red-600/25 hover:bg-red-700 hover:shadow-xl dark:bg-red-600 dark:hover:bg-red-700'
                  render={<Link to='/dashboard' />}
                >
                  {t('Get Started')}
                  <ArrowRight className='ml-2 size-4 transition-transform duration-200 group-hover:translate-x-1' />
                </Button>
              ) : (
                <Button
                  className='group rounded-xl bg-red-600 px-6 py-5 text-base font-semibold text-white shadow-lg shadow-red-600/25 hover:bg-red-700 hover:shadow-xl dark:bg-red-600 dark:hover:bg-red-700'
                  onClick={openSignUp}
                >
                  {t('Get Started')}
                  <ArrowRight className='ml-2 size-4 transition-transform duration-200 group-hover:translate-x-1' />
                </Button>
              )}
              {props.isAuthenticated ? (
                <Button
                  variant='outline'
                  className='rounded-xl border-2 border-border/60 bg-transparent px-6 py-5 text-base font-semibold hover:border-border hover:bg-muted/50'
                  render={<Link to='/pricing' />}
                >
                  {t('View Pricing')}
                </Button>
              ) : (
                <Button
                  variant='outline'
                  className='rounded-xl border-2 border-border/60 bg-transparent px-6 py-5 text-base font-semibold hover:border-border hover:bg-muted/50'
                  onClick={openSignIn}
                >
                  {t('View Pricing')}
                </Button>
              )}
            </div>
          </div>

          {/* Right: terminal demo */}
          <div
            className='landing-animate-fade-up w-full max-w-lg flex-1 opacity-0 md:max-w-xl'
            style={{ animationDelay: '200ms' }}
          >
            <HeroTerminalDemo />
          </div>
        </div>

        {/* Bottom: 4 feature cards */}
        <div className='mt-16 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6'>
          {features.map((f, i) => (
            <AnimateInView
              key={f.title}
              delay={i * 100}
              animation='fade-up'
              className='border-border/40 bg-muted/20 rounded-xl border p-5'
            >
              <div className='mb-2 flex items-center gap-2'>
                {f.icon}
                <h3 className='text-lg font-semibold'>{f.title}</h3>
              </div>
              <p className='text-muted-foreground text-base leading-relaxed'>
                {f.desc}
              </p>
            </AnimateInView>
          ))}
        </div>
      </div>
    </section>
  )
}
